import { ReactNode, useState } from 'react';
import { CaseDuplicatesContent } from './components/CaseDuplicatesContent';
import { useCaseDuplicateOpenHandler } from './hooks/useCaseDuplicateOpenHandler';
import { CaseDuplicatesTrigger } from './components/CaseDuplicatesTrigger';
import { useSearchableList } from '@/hooks/useSearchableList';
import { Case, CaseDuplicateType } from '@/api/apis/case';

export type CaseDuplicatesWrapperProps = {
  duplicates: CaseDuplicateType[];
  trigger: ReactNode;
  triggerType?: 'click' | 'hover';
  withSearch?: boolean;
  className?: string;
  onDuplicateClick?: (duplicate: Case) => void;
};

export const CaseDuplicatesList = ({
  duplicates,
  trigger,
  triggerType = 'click',
  withSearch = false,
  className = '',
  onDuplicateClick,
}: CaseDuplicatesWrapperProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    list: filteredDuplicates,
    searchValue,
    setSearchValue,
  } = useSearchableList(duplicates, ['caseName', 'caseNumber']);
  const { isLoadingCase, hideTooltip, handleDuplicateClick } = useCaseDuplicateOpenHandler();

  const handleDuplicateItemClick = (duplicate: CaseDuplicateType) => {
    setIsOpen(false);
    setSearchValue('');
    handleDuplicateClick(duplicate, (fullCase: Case) => {
      onDuplicateClick?.(fullCase);
    });
  };

  const shouldRender = triggerType === 'hover' || isOpen;
  const content = shouldRender ? (
    <CaseDuplicatesContent
      isLoading={isLoadingCase}
      filteredDuplicates={filteredDuplicates}
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      onItemClick={handleDuplicateItemClick}
      triggerType={triggerType}
      withSearch={withSearch}
      className={className}
      onClose={() => setIsOpen(false)}
    />
  ) : null;

  return (
    <div role="presentation" onClick={(e) => e.stopPropagation()}>
      <CaseDuplicatesTrigger
        trigger={trigger}
        triggerType={triggerType}
        hideTooltip={hideTooltip}
        content={content}
        className={className}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
      />
    </div>
  );
};
